import { lazy, Suspense } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth.js";
import { FicConnectionProvider } from "./fic-connection.js";
import { LoginPage } from "./LoginPage.js";
import { PasswordChangePage } from "./PasswordChangePage.js";
import { PersonalShell } from "./PersonalShell.js";
import { personalApp } from "./app-mode.js";
import { AccountRecoveryPage } from "./AccountRecoveryPage.js";
import { isMobileAppPath, MobileAppMetadata } from "./MobileAppEntry.js";

const AppShell = lazy(() =>
  import("./AppShell.js").then((module) => ({ default: module.AppShell })),
);
const MobileAppShell = lazy(() => import("./MobileAppShell.js"));
const PrivateAreaPage = lazy(() => import("./PrivateAreaPage.js"));
const EmployeeAreaPage = lazy(() => import("./EmployeeAreaPage.js"));

const AuthenticatedApp = () => {
  const auth = useAuth();
  const { pathname } = useLocation();
  const mobile = !personalApp && isMobileAppPath(pathname);
  if (auth.loading)
    return (
      <div className="boot-screen">
        <img src="/cem-logo.png" alt="CEM" />
        <p>
          {mobile
            ? "Apertura App CEM…"
            : "Preparazione dello spazio di lavoro…"}
        </p>
      </div>
    );
  if (auth.systemError !== null)
    return (
      <main className="boot-screen boot-error" role="alert">
        <img src="/cem-logo.png" alt="CEM" />
        <h1>{mobile ? "App CEM non disponibile" : "Gestionale non pronto"}</h1>
        <p>{auth.systemError}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Controlla di nuovo
        </button>
      </main>
    );
  return auth.user === null ? (
    <LoginPage mobile={mobile} />
  ) : auth.mustChangePassword ? (
    <PasswordChangePage mobile={mobile} />
  ) : personalApp ? (
    <PersonalShell />
  ) : pathname === "/area-privata" || pathname === "/area-privata/" ? (
    <Suspense
      fallback={<div className="boot-screen">Apertura area privata…</div>}
    >
      <PrivateAreaPage />
    </Suspense>
  ) : mobile ? (
    <Suspense fallback={<div className="boot-screen">Apertura App CEM…</div>}>
      <MobileAppShell />
    </Suspense>
  ) : auth.user.permissions.includes("employee-self:read") ? (
    <Suspense
      fallback={<div className="boot-screen">Apertura area dipendente…</div>}
    >
      <EmployeeAreaPage key={auth.user.id} />
    </Suspense>
  ) : (
    <FicConnectionProvider enabled={auth.user.permissions.includes("fic:read")}>
      <Suspense fallback={<div className="boot-screen">Caricamento…</div>}>
        <AppShell />
      </Suspense>
    </FicConnectionProvider>
  );
};

function AppContent() {
  const { pathname } = useLocation();
  const mobileRecovery =
    !personalApp && pathname === "/mobile/accesso/recupero";
  return !personalApp &&
    (pathname === "/accesso/recupero" || mobileRecovery) ? (
    <AccountRecoveryPage returnHref={mobileRecovery ? "/mobile" : "/"} />
  ) : (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export const App = () => (
  <BrowserRouter>
    {!personalApp && <MobileAppMetadata />}
    <AppContent />
  </BrowserRouter>
);
